import * as React from "react";
import { FormProvider, useWatch } from "react-hook-form";

import { ResourceFormActions } from "@/shared/components";
import {
  ResourceFormActionsRow,
  ResourceFormStatus,
  submitError,
  type SubmitError,
} from "@/shared/components/ResourceForm";
import {
  type AgentRecord,
  type CreateAgentRequest,
} from "@/shared/domain/agents";
import { useOrgEventModel } from "@/shared/domain/orgs/hooks";
import { RobotoButton } from "@/shared/foundation/primitives";

import {
  buildRequestTemplate,
  buildVariables,
} from "../Agents/buildRequestTemplate";

import styles from "./AgentForm.module.css";
import { type AgentFormOutput } from "./agentFormSchema";
import { ModelSection } from "./sections/ModelSection";
import { NameSection } from "./sections/NameSection";
import { RunsOnSection } from "./sections/RunsOnSection";
import { SkillsSection } from "./sections/SkillsSection";
import { useAgentEligibleSkills } from "./skills/useAgentEligibleSkills";
import { useAgentForm } from "./useAgentForm";

export type AgentFormProps = (
  | { mode: "create" }
  /** Seeded from the stored agent. Unlike a trigger's, its name stays editable. */
  | { mode: "edit"; initial: AgentRecord }
) & {
  /**
   * Receives the built request and resolves when the page's mutation settles.
   * The pending promise is the submitting state and a rejection becomes the
   * form's own server error, so neither is a separate prop.
   *
   * Create and edit send the same four fields; the page picks POST or PUT.
   */
  onSubmit: (request: CreateAgentRequest) => Promise<void>;
  onCancel?: () => void;
};

/** The wire payload, built by the same pair the old editor sent through. */
function buildAgentRequest(values: AgentFormOutput): CreateAgentRequest {
  const form = values;
  const description = values.description.trim();
  return {
    name: values.name,
    description: description === "" ? null : description,
    request_template: buildRequestTemplate(form),
    variables: buildVariables(form),
  };
}

/**
 * The whole create-or-edit agent form: one react-hook-form form over one
 * schema, laid out as the page a person reads top to bottom.
 *
 * Every section below reads and writes the form itself through
 * `useFormContext`, so nothing here hands values down. There is no read-only
 * mode — a page that only shows an agent is not this component.
 */
export function AgentForm(props: AgentFormProps) {
  const { onSubmit, onCancel } = props;
  // The schema cannot state two rules on its own: which skills the org offers,
  // and which events goal its event model allows. Both are fetched here and
  // handed to the resolver. The Skills section reads the same skills query for
  // its rows and its picker, and each goal card reads the event model for its
  // kind picker.
  const { ids: eligibleSkillIds } = useAgentEligibleSkills();
  const eventModel = useOrgEventModel();
  const form = useAgentForm({
    initial: props.mode === "edit" ? props.initial : undefined,
    eligibleSkillIds,
    eventModel,
  });

  // `handleSubmit` re-throws whatever the handler threw, so the page's refusal
  // is caught around the call rather than inside the handler.
  const submit = form.handleSubmit(async (values) => {
    await onSubmit(buildAgentRequest(values));
  });

  // The refusal is the page's answer, not a field's, and it is held here
  // rather than under the form's `root` error: any change after the first
  // submit re-validates the whole form, and that replaces every error the
  // schema did not produce.
  const [serverError, setServerError] = React.useState<SubmitError | null>(
    null,
  );
  const subject = useWatch({ control: form.control, name: "subject" });
  const submitLabel = props.mode === "create" ? "Create agent" : "Save changes";

  return (
    <FormProvider {...form}>
      <form
        className={styles.form}
        data-testid="agent-form"
        onSubmit={(event) => {
          // Every attempt starts without the last one's refusal, so a stale
          // message never sits beside a form the person has since fixed.
          setServerError(null);
          submit(event).catch((error: unknown) => {
            setServerError(submitError(error, props.mode, "agent"));
          });
        }}
        noValidate
      >
        {props.mode === "edit" && <NameSection />}
        <RunsOnSection />
        <SkillsSection />
        <ModelSection />
        {/* A new agent is named last, after the rest is filled in, so like
            its goals and kickoff message, the name appears only once what the
            agent runs on is picked. Mounting the name together with the
            kickoff message also registers the two fields in page order, the
            order in which a failed submit picks the field to focus. */}
        {props.mode === "create" && subject !== null && <NameSection />}
        {subject === null ? (
          <UnpickedSubjectActions
            submitLabel={submitLabel}
            onCancel={onCancel}
          />
        ) : (
          <ResourceFormActions
            submitLabel={submitLabel}
            idleHint={
              props.mode === "create"
                ? "Create the agent when it says what you want done."
                : "Save when the agent says what you want done."
            }
            submitKind={props.mode === "create" ? "create" : "save"}
            onCancel={onCancel}
            serverError={serverError}
          />
        )}
      </form>
    </FormProvider>
  );
}

interface UnpickedSubjectActionsProps {
  submitLabel: string;
  onCancel?: () => void;
}

/**
 * The actions row while nothing says what the agent runs on. Goals, the
 * kickoff message and the variables all wait on that choice, so there is
 * nothing yet a submit could report on: unlike the usual row, the submit is
 * disabled and the status says what to do first.
 */
function UnpickedSubjectActions({
  submitLabel,
  onCancel,
}: UnpickedSubjectActionsProps) {
  return (
    <ResourceFormActionsRow
      data-testid="resource-form-actions"
      status={
        <ResourceFormStatus data-testid="resource-form-status">
          Pick what the agent runs on first.
        </ResourceFormStatus>
      }
      buttons={
        <>
          {onCancel !== undefined && (
            <RobotoButton variant="secondary" onClick={onCancel}>
              Cancel
            </RobotoButton>
          )}
          <RobotoButton
            type="submit"
            disabled
            data-testid="resource-form-submit"
          >
            {submitLabel}
          </RobotoButton>
        </>
      }
    />
  );
}
